import {
  FLIGHT_DIRECTION,
  type Flight,
  type FlightDirection,
} from "@mozioinc/query";
import { ResponsiveAutocomplete } from "@mozioinc/ui";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { useDebounce } from "usehooks-ts";
import { flightQuery } from "../utils/query";
import { ReactComponent as FlightArrivingSvg } from "./assets/flight-arriving.svg";
import { ReactComponent as FlightDepartingSvg } from "./assets/flight-departing.svg";
import type { ResponsiveAutocompleteProps } from "@mozioinc/ui";

type FlightComboboxProps = {
  onValueChange?: (flight?: Flight) => void;
  direction: FlightDirection;
  airportCode: string;
  dateTime: Date;
} & Omit<ResponsiveAutocompleteProps, "items" | "onValueChange">;

const FlightCombobox = ({
  onValueChange,
  direction,
  airportCode,
  dateTime,
  ...props
}: FlightComboboxProps) => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce<string>(query, 400);
  const [loading, setLoading] = useState(false); // NOTE: this loading state is to include debounce time

  const parsedFlight = parseFlightQuery(debouncedQuery);

  const { data: flights = [], isFetching } = useQuery({
    queryKey: [
      "flights",
      parsedFlight?.airlineCode,
      airportCode,
      direction,
      dateTime,
    ],
    queryFn: () =>
      flightQuery.queryFlights({
        airlineCode: parsedFlight?.airlineCode || "",
        airportCode,
        flightDirection: direction,
        dateTime,
        flightNumber: parsedFlight?.flightNumber,
      }),
    enabled: debouncedQuery.length > 0 && Boolean(parsedFlight?.airlineCode),
  });

  const debounceDone = query === debouncedQuery;

  if (debounceDone && !isFetching && loading) {
    setLoading(false);
  }

  const handleInputChange = (value: string) => {
    if (value.length === 0) {
      return;
    }
    setQuery(value);
    setLoading(true);
  };

  const handleValueChange = (displayText: string) => {
    if (!displayText) {
      onValueChange?.();
      return;
    }

    const flight = flights.find(
      (f) => flightToString(f).toLowerCase() === displayText.toLowerCase(),
    );

    if (!flight) {
      console.error(
        `Flight not found for displayText: ${displayText}. This should not happen.`,
      );
      return;
    }

    onValueChange?.(flight);
  };

  const Icon =
    direction === FLIGHT_DIRECTION.DEPARTURE ? (
      <FlightDepartingSvg />
    ) : (
      <FlightArrivingSvg />
    );

  return (
    <ResponsiveAutocomplete
      {...props}
      items={flights.map((f) => {
        const flight = flightToString(f);

        return {
          value: flight,
          label: flight,
          icon: Icon,
        };
      })}
      icon={Icon}
      loading={loading}
      onValueChange={handleValueChange}
      onInputValueChange={handleInputChange}
      shouldFilter={false}
    />
  );
};

const parseFlightQuery = (query: string) => {
  const flightMatch = query
    .trim()
    .toUpperCase()
    .match(/^([A-Z]{2,3}|[A-Z]\d|\d[A-Z])?\s*(\d+)?$/);

  if (!flightMatch || !query) {
    return;
  }

  return {
    flightCode: `${flightMatch[1] || ""}${flightMatch[2] || ""}`,
    flightNumber: flightMatch[2] || "",
    airlineCode: flightMatch[1] || "",
  };
};

const flightToString = (flight: Flight, options?: { compact?: boolean }) => {
  const parts = [];

  if (flight.flightCode || flight.flightNumber) {
    if (flight.flightNumber && flight.airlineCode && flight.airlineName) {
      const flightPrefix = options?.compact
        ? flight.airlineCode || flight.airlineName
        : flight.airlineName === flight.airlineCode
        ? flight.airlineName
        : `${flight.airlineName}(${flight.airlineCode})`;
      parts.push(`${flightPrefix} ${flight.flightNumber}`);
    } else {
      parts.push(`${flight.flightCode}`);
    }
  }

  if (flight.origin && flight.destination && !options?.compact) {
    parts.push(`${flight.origin || ""} → ${flight.destination || ""}`);
    if (flight.time) {
      const formattedDate = format(flight.flightDateTime, "d MMM");
      parts.push(formattedDate, flight.time);
    }
  }

  return parts.join(" / ");
};

export { FlightCombobox };
